import dayjs from 'dayjs';
import {makeAutoObservable, runInAction} from 'mobx';

import {fetchServiceInfo} from '@/service/applyService';
import {ServiceStatusEnum} from '@/dicts/enum';

const initServiceInfoRaw = {
    total: 0,
    remainingCount: 0, // 剩余次数
    remainingDays: 0, // 剩余天数
    expiryDate: '',
    orderType: '', // 订单支付方式: prepay: 预付费; postpay：后付费
    hasMoreCombo: false,
    status: ServiceStatusEnum.NOT_OPENED, // 默认为未开通状态，确保初始状态下按钮禁用
};

// 用户信息类型定义
interface UserInfo {
    account_id?: string;
    plan?: string;
    term_end?: string;
    total: number;
    cost: number;
    order_type?: string;
    more?: number;
    status: number;
}

// API响应类型定义
interface ServiceInfoResponse {
    [key: string]: any;
    data?: UserInfo;
}

export class ServiceInfoStore {
    userInfoRaw: ServiceInfoResponse = {};
    serviceStatusRaw: number | null = null;
    serviceInfoRaw = initServiceInfoRaw;
    loading = true;
    error: string | null = null;

    private abortController: AbortController | null = null;


    constructor() {
        makeAutoObservable(this, {}, {autoBind: true});
    }

    get userInfo(): UserInfo {
        return this.userInfoRaw?.data || {} as UserInfo;
    }

    get serviceStatus(): number | null {
        return this.serviceStatusRaw;
    }

    get serviceInfo() {
        return this.serviceInfoRaw;
    }

    // 获取服务信息
    async getServiceInfo(): Promise<void> {
        // 取消之前的请求
        if (this.abortController) {
            this.abortController.abort();
        }

        this.abortController = new AbortController();

        this.loading = true;
        this.error = null;

        try {
            const res = await fetchServiceInfo();
            runInAction(() => {
                this.userInfoRaw = res || {};
                const userData = res.data || {} as UserInfo;
                this.serviceStatusRaw = this.setServiceStatus(userData);
                this.serviceInfoRaw = this.buildServiceInfo(userData);
                // userData存储到session0Storage
                userData.status && sessionStorage.setItem('serviceStatus', JSON.stringify(userData.status));
                this.loading = false;
            });
        } catch (error) {
            runInAction(() => {
                this.error = error instanceof Error ? error.message : '获取服务信息失败';
                this.loading = false;
            });
        } finally {
            // 确保loading状态一定会被重置
            this.loading = false;
        }
    }

    // 清理store数据
    clearStoreData = (): void => {
        this.userInfoRaw = {};
        this.serviceStatusRaw = null;
        this.serviceInfoRaw = initServiceInfoRaw;
        this.error = null;
    };

    // 设置服务状态
    private setServiceStatus(userInfo: UserInfo): number {
        this.serviceStatusRaw = userInfo.status;
        return userInfo.status;
    }

    // 构建服务信息对象
    private buildServiceInfo(userInfo: UserInfo): typeof initServiceInfoRaw {
        if (!userInfo.account_id) {
            return initServiceInfoRaw;
        }

        const endDay = dayjs(userInfo.term_end);
        const remainingDays = endDay.diff(dayjs(), 'day');
        const remainingCount = userInfo.total - userInfo.cost;

        return {
            total: userInfo.total,
            remainingCount,
            remainingDays: remainingDays < 0 ? 0 : remainingDays,
            expiryDate: userInfo.term_end || '',
            orderType: userInfo.order_type || '',
            hasMoreCombo: userInfo.more !== 0,
            status: userInfo.status,
        };
    }
}

export default new ServiceInfoStore();
